import { Alert, AlertTitle, AlertDescription } from "./components/ui/alert";
import { Button } from "./components/ui/button";

import { AlertTriangleIcon, RefreshCwIcon, CopyIcon } from "lucide-react";
import { toast } from "sonner";

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

export const ErrorFallback = ({ error, resetErrorBoundary }: ErrorFallbackProps) => {
  // When encountering an error in the development mode, rethrow it and don't display the boundary.
  // The parent UI will take care of showing a more helpful dialog.
  if (import.meta.env.DEV) throw error;

  // Log error to console in production for debugging
  console.error('[ErrorBoundary] Caught error:', error);

  const handleCopyErrorDetails = () => {
    const errorDetails = {
      message: error.message,
      stack: error.stack || 'No stack trace available',
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    };
    
    const errorText = `Error Report
---
Message: ${errorDetails.message}

Stack Trace:
${errorDetails.stack}

User Agent: ${errorDetails.userAgent}
URL: ${errorDetails.url}
Timestamp: ${errorDetails.timestamp}`;

    // Check if clipboard API is available (requires HTTPS or localhost)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(errorText).then(() => {
        toast.success('Error details copied to clipboard');
      }).catch(() => {
        toast.error('Failed to copy error details');
      });
    } else {
      // Fallback: show alert with error details if clipboard is not available
      toast.error('Clipboard not available. Error details logged to console.');
      console.log('Error details:', errorText);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <Alert variant="destructive" className="mb-6">
          <AlertTriangleIcon />
          <AlertTitle>The application has encountered a runtime error</AlertTitle>
          <AlertDescription>
            Something unexpected happened while running the application. The error details are shown below. Please contact support or try again later.
          </AlertDescription>
        </Alert>
        
        <div className="bg-card border rounded-lg p-4 mb-4">
          <h3 className="font-semibold text-sm text-muted-foreground mb-2">Error Message:</h3>
          <pre className="text-xs text-destructive bg-muted/50 p-3 rounded border overflow-auto max-h-32 mb-4">
            {error.message}
          </pre>
          
          {error.stack && (
            <>
              <h3 className="font-semibold text-sm text-muted-foreground mb-2">Stack Trace:</h3>
              <pre className="text-xs text-destructive bg-muted/50 p-3 rounded border overflow-auto max-h-64 font-mono">
                {error.stack}
              </pre>
            </>
          )}
        </div>
        
        <div className="flex gap-4">
          <Button 
            onClick={resetErrorBoundary} 
            className="flex-1"
            variant="outline"
          >
            <RefreshCwIcon />
            Try Again
          </Button>
          <Button 
            onClick={handleCopyErrorDetails} 
            className="flex-1"
            variant="outline"
          >
            <CopyIcon />
            Copy Error Details
          </Button>
        </div>
      </div>
    </div>
  );
}
